/**
 * 個人レポート v2 の表示部品。
 *
 * 正典: apps/web/docs/新方針20260821/モック_GB人財レポート_本人向け_v2.html
 *
 * 【方針】A4横2ページの器（Gb74PersonalReportPdf）とPDF化の経路はそのまま使い、
 * p2 の中身だけをこの部品に差し替える。v1 の部品（Gb74ReportBlocks）は温存する。
 *
 * 【モックの数値は使わない】仕様書09節のとおり、モックの役割スコアは仮値で
 * 算出式から逆算されていない。数値は必ず gb74ReportV2 の算出結果を使う。
 * モックはレイアウトの参照にとどめる。
 */
import React from 'react';
import type { RoleScoreV2, TraitScore, Gb74ReportV2 } from '../utils/gb74ReportV2';
import { getComplementSelf, type PersonalTextsV2 } from '../utils/personalTextsV2';

/** 目盛り1〜5のバー1本（持ち味の出方）。中央3に基準線を置く。 */
const TraitBar: React.FC<{ trait: TraitScore; emphasis?: boolean }> = ({ trait, emphasis }) => {
  const pct = Math.max(0, Math.min(100, ((trait.value - 1) / 4) * 100));
  return (
    <div className={`gb74v2-trait${emphasis ? ' _em' : ''}`}>
      <div className="gb74v2-trait-name">{trait.key}</div>
      <div className="gb74v2-trait-bar">
        <div className="gb74v2-trait-mid" />
        <div className="gb74v2-trait-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="gb74v2-trait-val">{trait.value.toFixed(1)}</div>
    </div>
  );
};

/**
 * セクション02 あなたの持ち味の出方。
 * 4本すべてをバーで出し、最上位と最下位にだけ二面表現（PA15）を添える。
 */
export const Gb74TraitsBlock: React.FC<{ report: Gb74ReportV2; texts: PersonalTextsV2 }> = ({
  report,
  texts,
}) => (
  <>
    <div className="gb74v2-traits">
      {report.traits.map((t) => (
        <TraitBar
          key={t.key}
          trait={t}
          emphasis={t.key === report.traitTop || t.key === report.traitLow}
        />
      ))}
      <div className="gb74v2-trait-scale">
        <span>1</span>
        <span>3</span>
        <span>5</span>
      </div>
    </div>
    {texts.traitTop && (
      <div className="gb74v2-note-card">
        <div className="gb74v2-note-ttl">いちばん出ている {report.traitTop}</div>
        <div className="gb74v2-note-txt">{texts.traitTop.本文}</div>
      </div>
    )}
    {texts.traitLow && (
      <div className="gb74v2-note-card">
        <div className="gb74v2-note-ttl">いちばん控えめ {report.traitLow}</div>
        <div className="gb74v2-note-txt">{texts.traitLow.本文}</div>
      </div>
    )}
  </>
);

/**
 * 役割1件。バーのみで、点数は出さない。
 *
 * 【点数を出さない理由】役割スコアは個人内で相対化した値で、他者比較の基準がない。
 * 数値を並べると「59と55」のような僅差が意味のある差に見え、順位の断定にもつながる。
 * 程度が伝わるバーだけを残す。上位グループは色で強調する。
 */
const RoleRow: React.FC<{ role: RoleScoreV2 }> = ({ role }) => (
  <div className={`gb74v2-role${role.top ? ' _top' : ''}`}>
    <div className="gb74v2-role-name">{role.name}</div>
    <div className="gb74v2-role-bar">
      <div className="gb74v2-role-fill" style={{ width: `${role.score}%` }} />
    </div>
  </div>
);

/**
 * セクション04 あなたが力を出せる役割。
 * 上位グループは1件のこともあるため（仕様書09節）、件数に依存しない作りにする。
 */
export const Gb74RolesBlockV2: React.FC<{ report: Gb74ReportV2; texts: PersonalTextsV2 }> = ({
  report,
  texts,
}) => {
  const top = report.roles.filter((r) => r.top);
  const rest = report.roles.filter((r) => !r.top);
  return (
    <>
      <div className="gb74v2-role-group">◆ 力が出る役割</div>
      {top.map((r) => (
        <RoleRow key={r.name} role={r} />
      ))}
      {/* 説明文は上位から最大3件まで。上位グループは算出次第で5件以上になることがあり、
          全件出すとA4 1ページに収まらないため（モックも3件想定）。 */}
      {top.slice(0, 3).map((r) =>
        texts.roleBands[r.name] ? (
          <div className="gb74v2-role-desc" key={`d-${r.name}`}>
            <b>{r.name}</b>
            {texts.roleBands[r.name]?.本文}
          </div>
        ) : null
      )}
      {rest.length > 0 && (
        <>
          <div className="gb74v2-role-group _sub">— 以下は参考です</div>
          {rest.map((r) => (
            <RoleRow key={r.name} role={r} />
          ))}
        </>
      )}
      {/* 根拠は上位1つと最下位1つにだけ添える（固有ファセットのみを使う） */}
      {texts.roleReasonTop && (
        <div className="gb74v2-reason">
          {report.roleReasonTop}が高いのは、{texts.roleReasonTop.本文}
        </div>
      )}
      {texts.roleReasonLow && (
        <div className="gb74v2-reason">
          {report.roleReasonLow}が低いのは、{texts.roleReasonLow.本文}
        </div>
      )}
    </>
  );
};

/**
 * セクション05 あなたを輝かせるには？
 * 伸ばすところ（上位役割の段階文言）と気をつけるところ（PA03）＋補い方（PA04）。
 */
export const Gb74ShineBlock: React.FC<{ report: Gb74ReportV2; texts: PersonalTextsV2 }> = ({
  report,
  texts,
}) => {
  const topRole = report.topRoles[0];
  return (
    <>
      {/* 伸ばすところ = 思考スタイル(PA01)＋行動スタイル(PA02)＋上位役割（仕様書06節）。
          PA05の段階文言は中央値27文字と短く、ここには足りないため使わない。 */}
      <div className="gb74v2-shine _good">
        <div className="gb74v2-shine-ttl">◆ 伸ばすところ</div>
        <div className="gb74v2-shine-txt">
          {texts.thought?.本文}
          {texts.action?.本文 ? ` ${texts.action.本文}` : ''}
          {topRole && <> この力は「{topRole}」の場面でいちばん出ます。</>}
        </div>
      </div>
      {texts.caution && (
        <div className="gb74v2-shine _care">
          <div className="gb74v2-shine-ttl">◇ 気をつけるところ</div>
          <div className="gb74v2-shine-txt">{texts.caution.本文}</div>
        </div>
      )}
      {/* 補い方は最低軸で選ぶ。本人向けの言い回しを使う（DBのPA04は上司向けの書き方） */}
      <div className="gb74v2-shine _fix">
        <div className="gb74v2-shine-ttl">{texts.complement?.見出し ?? '補い方'}</div>
        <div className="gb74v2-shine-txt">{getComplementSelf(report.lowestAxis)}</div>
      </div>
    </>
  );
};
